import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Natural-order comparator for list display. Sorts numerically when values look
 * like numbers ("1","2","10") and alphabetically otherwise ("A","B","C"), which
 * is what we want for sheet IDs, names, and other user-facing listings.
 * Use as: [...items].sort((a, b) => naturalCompare(a.sheetId, b.sheetId)).
 */
export function naturalCompare(a: string, b: string): number {
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** Convenience: sort an array by a string key in natural order (non-mutating). */
export function sortByNatural<T>(items: T[], key: (item: T) => string): T[] {
  return [...items].sort((a, b) => naturalCompare(key(a), key(b)));
}

/**
 * Format an ISO instant in a specific facility timezone (falls back to the
 * viewer's local zone if none given). Schedule times are stored as UTC and must
 * be shown in the FACILITY's zone, not the viewer's, or games appear time-shifted.
 */
export function fmtInTz(iso: string, tz?: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      ...(tz ? { timeZone: tz } : {}),
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return new Date(iso).toLocaleString();
  }
}

// ─── Facility-timezone datetime inputs ───────────────────────────────────────
// `<input type="datetime-local">` reads and writes the BROWSER's wall time, but
// every time in CSM means a time at the rink. An admin in Ontario setting
// "5:00 PM" for a facility in Alberta means 5:00 PM there, not 5:00 PM here —
// getting this wrong silently shifted end times by the offset between the two.
// Use these to convert in both directions so the input is always rink time.

/** An instant → the value for a `datetime-local` input, as wall time in `tz`. */
export function toZonedInputValue(iso: string | null | undefined, tz?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  if (!tz) {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d);
    const get = (t: string) => parts.find(p => p.type === t)?.value ?? '00';
    const hour = get('hour') === '24' ? '00' : get('hour'); // midnight edge case
    return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
  } catch {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}

/**
 * A `datetime-local` value (or "YYYY-MM-DD HH:MM") read as wall time in `tz`
 * → the instant it refers to. Null when unparseable.
 *
 * Two passes: guess the instant by treating the wall time as UTC, measure the
 * zone's offset there, correct, then re-measure — which settles the case where
 * the first guess lands on the far side of a DST change. The ambiguous repeated
 * hour of a fall-back resolves to the first occurrence.
 */
export function zonedInputToInstant(wall: string, tz?: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{1,2}):(\d{2})/.exec((wall ?? '').trim());
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]), h = Number(m[4]), mi = Number(m[5]);
  if (!tz) {
    const local = new Date(y, mo - 1, d, h, mi);
    return isNaN(local.getTime()) ? null : local;
  }
  const naive = Date.UTC(y, mo - 1, d, h, mi);
  try {
    const offsetAt = (t: number): number => {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      }).formatToParts(new Date(t));
      const g = (k: string) => Number(parts.find(p => p.type === k)?.value ?? 0);
      const hh = g('hour') === 24 ? 0 : g('hour');
      return Date.UTC(g('year'), g('month') - 1, g('day'), hh, g('minute'), g('second')) - t;
    };
    let ts = naive - offsetAt(naive);
    ts = naive - offsetAt(ts);
    return new Date(ts);
  } catch {
    return new Date(naive);
  }
}

/** "2d 3h left" / "3h 12m left" / "14m left" / "expired". Timezone-independent. */
export function timeRemaining(iso: string | null | undefined): string {
  if (!iso) return '';
  const ms = new Date(iso).getTime() - Date.now();
  if (isNaN(ms)) return '';
  if (ms <= 0) return 'expired';
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h left`;
  if (hours > 0) return `${hours}h ${mins % 60}m left`;
  return `${mins}m left`;
}
